/*
  Label Lique FAQ page (sections/ll-faq.liquid): live search over questions and answers, the
  "veel gezocht" buttons, and highlighting the topic in the sidebar that is in view.
  Without JavaScript the page still works: the accordions are plain <details>.
*/
(() => {
  if (customElements.get('ll-faq')) return;

  // Lowercase and strip accents, so "cadeau" also finds "Cadeáu".
  const normalize = (text) =>
    text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');

  class LlFaq extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-faq-input]');
      this.status = this.querySelector('[data-faq-status]');
      this.statusText = this.querySelector('[data-faq-status-text]');
      this.clearButton = this.querySelector('.ll-faq__search-clear');
      this.topics = this.querySelector('[data-faq-topics]');
      this.groups = [...this.querySelectorAll('[data-faq-group]')];
      this.navItems = [...this.querySelectorAll('[data-faq-nav]')];
      this.items = [...this.querySelectorAll('[data-faq-item]')].map((el) => ({
        el,
        text: normalize(el.textContent),
      }));

      this.input.addEventListener('input', () => this.search(this.input.value));
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.input.value) {
          event.preventDefault();
          this.reset();
        }
      });
      this.querySelectorAll('[data-faq-reset]').forEach((button) =>
        button.addEventListener('click', () => this.reset())
      );
      this.querySelectorAll('[data-faq-term]').forEach((button) =>
        button.addEventListener('click', () => {
          this.input.value = button.dataset.faqTerm;
          this.search(this.input.value);
        })
      );

      this.observeTopics();

      // Theme editor: open the question that is selected in the sidebar.
      this.onBlockSelect = (event) => {
        const item = event.target.closest?.('[data-faq-item]');
        if (item && this.contains(item)) item.open = true;
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    search(value) {
      const query = normalize(value.trim());
      if (!query) {
        this.showAll();
        return;
      }

      const terms = query.split(/\s+/);
      let found = 0;
      this.items.forEach(({ el, text }) => {
        const match = terms.every((term) => text.includes(term));
        el.hidden = !match;
        if (match) found++;
        // Open matches so the answer shows right away; remember which ones we opened.
        if (match && !el.open) {
          el.open = true;
          el.dataset.faqOpened = '';
        } else if (!match && el.hasAttribute('data-faq-opened')) {
          el.open = false;
          delete el.dataset.faqOpened;
        }
      });
      this.updateGroups();

      const template = found === 0 ? this.dataset.noResults : found === 1 ? this.dataset.resultOne : this.dataset.resultOther;
      this.statusText.textContent = template.replace('[count]', found).replace('[terms]', value.trim());
      this.status.hidden = false;
      this.clearButton.hidden = false;
      if (this.topics) this.topics.hidden = true;
    }

    showAll() {
      this.items.forEach(({ el }) => {
        el.hidden = false;
        if (el.hasAttribute('data-faq-opened')) {
          el.open = false;
          delete el.dataset.faqOpened;
        }
      });
      this.updateGroups();
      this.status.hidden = true;
      this.clearButton.hidden = true;
      if (this.topics) this.topics.hidden = false;
    }

    reset() {
      this.input.value = '';
      this.showAll();
      this.input.focus();
    }

    // Hide topics without visible questions, in the list and in the sidebar, and keep the
    // sidebar counts in line with what's shown.
    updateGroups() {
      this.groups.forEach((group) => {
        group.hidden = !group.querySelector('[data-faq-item]:not([hidden])');
      });
      this.navItems.forEach((item) => {
        const group = document.getElementById(item.dataset.faqNav);
        item.hidden = !group || group.hidden;
        const count = item.querySelector('.ll-faq__nav-count');
        if (group && count) count.textContent = group.querySelectorAll('[data-faq-item]:not([hidden])').length;
      });
    }

    observeTopics() {
      if (!this.navItems.length || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.navItems.forEach((item) => {
              const link = item.querySelector('a');
              if (item.dataset.faqNav === entry.target.id) {
                link.setAttribute('aria-current', 'true');
              } else {
                link.removeAttribute('aria-current');
              }
            });
          });
        },
        { rootMargin: '-30% 0px -60% 0px' }
      );
      this.groups.forEach((group) => {
        if (group.id) this.observer.observe(group);
      });
    }
  }

  customElements.define('ll-faq', LlFaq);
})();
